import { useState } from 'react';
import { useAttachProduct, useSearchStore, useStores } from '../../api/coverage';
import type { AttachedProduct, StoreSearchHit } from '../../api/coverage';

/** Find a product yourself, when the automatic pass could not.
 *
 * Two ways in, because it fails two different ways. Either the matcher
 * searched the wrong word — a shop calls scallions "green onions" — and a
 * better one finds it immediately; or the shop's search is the problem, and
 * the only way through is to open the product on the site and paste its
 * address.
 */
export default function ProductFinder({
  ingredientId,
  ingredientName,
  storeId,
}: {
  ingredientId: number;
  ingredientName: string;
  /* The shop the page is reviewing. Chosen once at the top rather than per
     row: a match is a claim about one shop's shelf, and picking the shop
     again on every ingredient invited attaching a product to the wrong one. */
  storeId: number | null;
}) {
  const { data: stores } = useStores();
  const search = useSearchStore();
  const attach = useAttachProduct();

  const [term, setTerm] = useState(ingredientName);
  const [url, setUrl] = useState('');

  const attached = attach.data as AttachedProduct | undefined;

  const activeStore = storeId;
  const storeName = stores?.find((store) => store.id === activeStore)?.name ?? 'the shop';

  function attachHit(hit: StoreSearchHit) {
    if (activeStore == null) return;
    attach.mutate({
      storeId: activeStore,
      ingredient: ingredientId,
      product_id: hit.product_id,
      product_name: hit.product_name,
      size: hit.size,
      regular_price: hit.regular_price,
      promo_price: hit.promo_price,
      product_url: hit.product_url,
      image_url: hit.image_url,
      term,
    });
  }

  return (
    <details className="finder">
      <summary>Find it myself at {storeName}</summary>

      <div className="finder__row">
        <input
          value={term}
          placeholder="try another word — “green onions”"
          aria-label={`Search the shop for ${ingredientName}`}
          onChange={(event) => setTerm(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === 'Enter' && activeStore != null && term.trim()) {
              search.mutate({ storeId: activeStore, term: term.trim() });
            }
          }}
        />
        <button
          type="button"
          className="btn btn--small"
          disabled={search.isPending || !term.trim() || activeStore == null}
          onClick={() => search.mutate({ storeId: activeStore!, term: term.trim() })}
        >
          {search.isPending ? 'Asking the shop…' : 'Search'}
        </button>
      </div>

      {search.isError && (
        <p className="finder__error">{(search.error as Error).message}</p>
      )}

      {search.data && search.data.results.length === 0 && (
        <p className="muted small">
          {storeName} returned nothing for “{search.data.term}”.
        </p>
      )}

      {search.data && search.data.results.length > 0 && (
        <ul className="finder__results">
          {search.data.results.map((hit) => (
            <li key={hit.product_id}>
              {hit.image_url ? (
                <img src={hit.image_url} alt="" width={40} height={40} loading="lazy" />
              ) : (
                <span className="review-thumb review-thumb--empty" aria-hidden="true" />
              )}
              <span className="finder__name">
                {hit.product_url ? (
                  <a href={hit.product_url} target="_blank" rel="noreferrer">
                    {hit.product_name}
                  </a>
                ) : (
                  hit.product_name
                )}
                {hit.size && <em> · {hit.size}</em>}
              </span>
              <span className="finder__price">
                {hit.promo_price
                  ? `$${hit.promo_price}`
                  : hit.regular_price
                    ? `$${hit.regular_price}`
                    : '—'}
              </span>
              <button
                type="button"
                className="btn btn--small btn--primary"
                disabled={attach.isPending}
                onClick={() => attachHit(hit)}
              >
                This one
              </button>
            </li>
          ))}
        </ul>
      )}

      <div className="finder__row finder__paste">
        <input
          value={url}
          placeholder={`…or paste the product's address from ${storeName}`}
          aria-label={`Paste a store link for ${ingredientName}`}
          onChange={(event) => setUrl(event.target.value)}
        />
        <button
          type="button"
          className="btn btn--small"
          disabled={attach.isPending || !url.trim() || activeStore == null}
          onClick={() =>
            attach.mutate({
              storeId: activeStore!,
              ingredient: ingredientId,
              product_url: url.trim(),
              product_name: ingredientName,
            })
          }
        >
          Use this link
        </button>
      </div>

      {attach.isError && (
        <p className="finder__error">{(attach.error as Error).message}</p>
      )}
      {attach.isSuccess && (
        /* Say what was actually attached, in the three states this really has.
           The old wording collapsed the last two and promised that "the next
           refresh will fill them in" — which is false for exactly the product
           most likely to be pasted here: one the shop's own catalogue does not
           list, so no refresh will ever see it. */
        <p className={attached?.looked_up ? 'finder__ok' : 'finder__error'}>
          {!attached?.looked_up ? (
            <>
              Saved the link, but {storeName} could not be asked what it is, so it will
              show under the ingredient's own name until something can read it.
            </>
          ) : attached.regular_price ? (
            <>
              Attached <strong>{attached.product_name}</strong>
              {attached.size ? ` · ${attached.size}` : ''}
              {` · $${attached.regular_price}`} — pinned, so the nightly refresh will
              leave it alone.
            </>
          ) : (
            <>
              Attached <strong>{attached.product_name}</strong>
              {attached.size ? ` · ${attached.size}` : ''} — but no price came back with
              it, so it will sit on the list without one. That is not a statement about
              whether {storeName} stocks it: the shop only quotes a price to a browser
              that has picked a store, and this is not one.
            </>
          )}
        </p>
      )}
    </details>
  );
}
